import { merge } from 'lodash';
import configJson from '../config.json';

export enum BuildType {
  DEBUG = 'debug',
  RELEASE = 'release',
}

export type DeploymentEnv = 'development' | 'staging' | 'production';

type CommonConfig = Readonly<{
  isDebugBuild: boolean;
  isReleaseBuild: boolean;
}>;

type CommonEnvironmentConfig = Readonly<{
  frontendWeb: {
    baseUrl: string;
  };
  canadaPost: {
    apiKey: string;
  };
  googleMaps: {
    apiKey: string;
    version: string;
  };
  knownSourceIds: string[];
}>;

export type CommonAppConfig = CommonConfig & CommonEnvironmentConfig;

const environmentConfigs = configJson as Record<DeploymentEnv, CommonEnvironmentConfig>;

export const FrontendCommonConfig = computeConfig(getDeploymentEnvironment());

function computeConfig(environment: string): CommonAppConfig {
  const buildType = getBuildType();

  const commonConfig: CommonConfig = {
    isDebugBuild: buildType === BuildType.DEBUG,
    isReleaseBuild: buildType === BuildType.RELEASE,
  };
  const commonEnvironmentConfig: CommonEnvironmentConfig = environmentConfigs[environment] || {};

  return merge({}, commonConfig, commonEnvironmentConfig) as CommonAppConfig;
}

function getBuildType(): BuildType {
  const buildEnvironment = getBuildEnvironment();
  if (buildEnvironment === undefined || buildEnvironment === 'development') {
    return BuildType.DEBUG;
  }

  return BuildType.RELEASE;
}

/**
 * The `process.env.NODE_ENV` variable is a special variable "injected" by Webpack
 * at runtime. The word "injected" is in double quotes for a good reason. Indeed,
 * the variable is not really injected, it's really a search & replace at build time
 * that is performed by Webpack. Meaning on the final bundle, this code will look
 * like `return "production"` (assuming `process.env.NODE_ENV` equals `production`).
 */
export function getBuildEnvironment() {
  return process.env.NODE_ENV;
}

/**
 * The `process.env.DEPLOYMENT_ENVIRONMENT` variable is a special variable "injected" by Webpack
 * at runtime. The word "injected" is in double quotes for a good reason. Indeed,
 * the variable is not really injected, it's really a search & replace at build time
 * that is performed by Webpack. Meaning on the final bundle, this code will look
 * like `return "production"` (assuming `process.env.DEPLOYMENT_ENVIRONMENT` equals
 * `production`).
 */
export function getDeploymentEnvironment() {
  return process.env.DEPLOYMENT_ENVIRONMENT || 'production';
}
